/**
 * Main deployment wizard dialog
 */

import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { X, Rocket } from 'lucide-react';
import { useDeploymentWizard } from '@/hooks/useDeploymentWizard';
import { DeploymentProgress } from './DeploymentProgress';
import { PrerequisitesStep } from './PrerequisitesStep';
import { EnvironmentStep } from './EnvironmentStep';
import { ProvisioningStep } from './ProvisioningStep';
import { PostInstallStep } from './PostInstallStep';
import { DeploymentLogsPanel } from './DeploymentLogsPanel';
import type { DeploymentTarget } from '@/lib/deployment/types';
import type { Blueprint } from '@/lib/blueprints/generator';

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  blueprint: Blueprint;
  target: DeploymentTarget;
};

export function DeploymentWizardDialog({ open, onOpenChange, blueprint, target }: Props) {
  const wizard = useDeploymentWizard(target, blueprint.id);
  const [hasStarted, setHasStarted] = useState(false);
  
  const isDellStack = blueprint.id === 'dell-inference-stack';

  const handleStart = () => {
    setHasStarted(true);
    wizard.startDeployment();
    wizard.updateStepStatus('prerequisites', 'in-progress');
  };

  const handlePrerequisitesComplete = () => {
    wizard.updateStepStatus('prerequisites', 'completed');
    const nextStep = isDellStack ? 'configure' : 'environment';
    wizard.setCurrentStep(nextStep);
    wizard.updateStepStatus(nextStep, 'in-progress');
    wizard.addLog({
      level: 'success',
      message: 'Prerequisites check completed',
      step: 'prerequisites',
    });
  };

  const handlePrerequisitesSkip = () => {
    wizard.updateStepStatus('prerequisites', 'skipped');
    const nextStep = isDellStack ? 'configure' : 'environment';
    wizard.setCurrentStep(nextStep);
    wizard.updateStepStatus(nextStep, 'in-progress');
    wizard.addLog({
      level: 'warn',
      message: 'Prerequisites check skipped',
      step: 'prerequisites',
    });
  };

  const handleEnvironmentComplete = (config: Record<string, string>) => {
    wizard.updateConfig(config);
    const currentStep = isDellStack ? 'configure' : 'environment';
    wizard.updateStepStatus(currentStep, 'completed');
    wizard.addLog({
      level: 'success',
      message: isDellStack ? 'iDRAC configuration saved' : 'Environment configured successfully',
      step: currentStep,
    });
    
    if (isDellStack) {
      wizard.setCurrentStep('deploy');
      wizard.updateStepStatus('deploy', 'in-progress');
    } else {
      wizard.setCurrentStep('complete');
      wizard.completeDeployment();
    }
  };

  const handleEnvironmentBack = () => {
    wizard.setCurrentStep('prerequisites');
    const currentStep = isDellStack ? 'configure' : 'environment';
    wizard.updateStepStatus(currentStep, 'pending');
  };

  const handleProvisioningComplete = () => {
    wizard.updateStepStatus('deploy', 'completed');
    wizard.setCurrentStep('verify');
    wizard.updateStepStatus('verify', 'in-progress');
    wizard.addLog({
      level: 'success',
      message: 'Infrastructure provisioning completed',
      step: 'deploy',
    });
  };

  const handleProvisioningBack = () => {
    wizard.setCurrentStep('configure');
    wizard.updateStepStatus('deploy', 'pending');
  };

  const handlePostInstallComplete = () => {
    wizard.updateStepStatus('verify', 'completed');
    wizard.setCurrentStep('complete');
    wizard.completeDeployment();
  };

  const handlePostInstallBack = () => {
    wizard.setCurrentStep('deploy');
    wizard.updateStepStatus('verify', 'pending');
  };

  const handleClose = () => {
    wizard.resetWizard();
    setHasStarted(false);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[90vh] p-0">
        <DialogHeader className="p-6 pb-4 border-b">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-primary/10">
                <Rocket className="h-5 w-5 text-primary" />
              </div>
              <div>
                <DialogTitle>Deploy Infrastructure</DialogTitle>
                <DialogDescription>
                  {blueprint.name} deployment wizard
                </DialogDescription>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant="outline">{target}</Badge>
              <Button variant="ghost" size="icon" onClick={handleClose}>
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </DialogHeader>

        <div className="grid grid-cols-3 gap-6 p-6 overflow-hidden">
          {/* Left: Progress Tracker */}
          <div className="col-span-1">
            <DeploymentProgress
              steps={wizard.state.steps}
              currentStep={wizard.state.currentStep}
            />
          </div>

          {/* Right: Step Content */}
          <div className="col-span-2">
            <ScrollArea className="h-[60vh] pr-4">
              {!hasStarted && (
                <div className="space-y-6">
                  <div className="p-6 rounded-lg border-2 border-dashed border-primary/20 bg-primary/5">
                    <h3 className="font-semibold mb-2">Ready to Deploy?</h3>
                    <p className="text-sm text-muted-foreground mb-4">
                      This wizard will guide you through deploying your infrastructure step-by-step.
                      We'll check prerequisites, configure your environment, and deploy your services.
                    </p>
                    <Button onClick={handleStart} className="w-full">
                      <Rocket className="h-4 w-4 mr-2" />
                      Start Deployment
                    </Button>
                  </div>

                  <DeploymentLogsPanel logs={[]} />
                </div>
              )}

              {hasStarted && wizard.state.currentStep === 'prerequisites' && (
                <PrerequisitesStep
                  target={target}
                  blueprintId={blueprint.id}
                  onComplete={handlePrerequisitesComplete}
                  onSkip={handlePrerequisitesSkip}
                />
              )}

              {hasStarted && (wizard.state.currentStep === 'environment' || wizard.state.currentStep === 'configure') && (
                <EnvironmentStep
                  blueprintId={blueprint.id}
                  onComplete={handleEnvironmentComplete}
                  onBack={handleEnvironmentBack}
                />
              )}

              {hasStarted && wizard.state.currentStep === 'deploy' && (
                <ProvisioningStep
                  config={wizard.state.config}
                  onComplete={handleProvisioningComplete}
                  onBack={handleProvisioningBack}
                />
              )}

              {hasStarted && wizard.state.currentStep === 'verify' && (
                <PostInstallStep
                  config={wizard.state.config}
                  onComplete={handlePostInstallComplete}
                  onBack={handlePostInstallBack}
                />
              )}

              {hasStarted && wizard.state.currentStep === 'complete' && (
                <div className="space-y-6">
                  <div className="p-6 rounded-lg bg-green-50 dark:bg-green-950/20 border-2 border-green-200 dark:border-green-800">
                    <h3 className="font-semibold text-green-900 dark:text-green-100 mb-2">
                      Deployment Complete!
                    </h3>
                    <p className="text-sm text-green-800 dark:text-green-200">
                      Your infrastructure has been deployed successfully.
                    </p>
                  </div>

                  <DeploymentLogsPanel logs={wizard.state.logs} />

                  <Button onClick={handleClose} className="w-full">
                    Close
                  </Button>
                </div>
              )}
            </ScrollArea>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
